<script setup lang="ts">
	/**
	 * Filtro de opción múltiple (cadena, municipio, zona): pastilla que abre una lista con
	 * casillas, cuántos hoteles hay en cada opción y buscador cuando son muchas.
	 * Las opciones salen de los resultados cargados (helpers/hotelFilters.ts).
	 */
	import { computed, ref } from 'vue'

	import { Checkbox } from '@/components/ui/checkbox'

	import { type FilterOption, normalizeKey } from '../helpers/hotelFilters'
	import FilterPill from './FilterPill.vue'

	const props = defineProps<{
		label: string
		icon: string
		/** Quien lo usa no lo pinta si viene vacío (ver HotelFiltersForm). */
		options: FilterOption[]
		/** Aviso al pie de la lista, con el estilo de CheapestRateNotice. */
		note?: string
	}>()

	const selected = defineModel<string[]>({ required: true })

	const SEARCH_THRESHOLD = 8

	const query = ref('')

	const visibleOptions = computed(() => {
		const term = normalizeKey(query.value)
		if (!term) return props.options
		return props.options.filter((option) =>
			normalizeKey(option.label).includes(term)
		)
	})

	// Con una sola opción elegida la pastilla dice cuál; con varias, la etiqueta y el globo.
	const singleLabel = computed(() =>
		selected.value.length === 1 ?
			(props.options.find((option) => option.key === selected.value[0])
				?.label ?? null)
		:	null
	)

	function toggle(key: string, checked: boolean | 'indeterminate') {
		selected.value =
			checked === true ?
				[...selected.value, key]
			:	selected.value.filter((value) => value !== key)
	}
</script>

<template>
	<FilterPill
		:label="label"
		:icon="icon"
		:value="singleLabel"
		:count="selected.length > 1 ? selected.length : 0"
		@clear="selected = []"
		@close="query = ''"
	>
		<input
			v-if="options.length > SEARCH_THRESHOLD"
			v-model="query"
			type="search"
			:placeholder="`Buscar ${label.toLowerCase()}`"
			:aria-label="`Buscar ${label.toLowerCase()}`"
			class="w-full rounded-lg border-0 px-2.5 py-1.5 text-[13px] text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#021F58]/30"
		/>

		<ul class="-mx-1 max-h-64 overflow-y-auto">
			<li
				v-for="option in visibleOptions"
				:key="option.key"
			>
				<label
					class="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] text-gray-800 hover:bg-gray-50"
				>
					<Checkbox
						:model-value="selected.includes(option.key)"
						@update:model-value="(checked) => toggle(option.key, checked)"
					/>
					<span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
					<span class="shrink-0 text-[12px] tabular-nums text-gray-400">{{
						option.count
					}}</span>
				</label>
			</li>
			<li
				v-if="visibleOptions.length === 0"
				class="px-2 py-3 text-center text-[12px] text-gray-500"
			>
				Sin coincidencias.
			</li>
		</ul>

		<p
			v-if="note"
			class="flex gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-[12px] leading-4 text-amber-900 ring-1 ring-inset ring-amber-200"
		>
			<span
				class="material-symbols-outlined shrink-0 text-[16px] leading-4"
				aria-hidden="true"
				>info</span
			>
			<span>{{ note }}</span>
		</p>
	</FilterPill>
</template>
